import { NUMERIC_DISPLAY_MODES, type NumericDisplayMode } from '@neptune/shared/common-util';

export type TableWidgetDisplayMode = NumericDisplayMode;

export type TableWidgetStepRange = {
  start?: number;
  end?: number;
};

export type TableWidgetOptions = {
  displayMode: TableWidgetDisplayMode;
  stepRange: TableWidgetStepRange;
};

export const TABLE_WIDGET_DISPLAY_MODES = NUMERIC_DISPLAY_MODES;
export const TABLE_WIDGET_DISPLAY_MODE_ITEMS = [
  { value: 'scientific', label: 'Scientific' },
  { value: 'decimal', label: 'Decimal' },
  { value: 'auto', label: 'Auto' },
  { value: 'raw', label: 'Raw' },
] as const satisfies readonly { value: TableWidgetDisplayMode; label: string }[];
export const TABLE_WIDGET_MAX_VISIBLE_STEPS = 10;

export const DEFAULT_TABLE_CONFIG: TableWidgetOptions = {
  displayMode: 'decimal',
  stepRange: {
    start: 0,
    end: 0,
  },
};

export function isTableWidgetDisplayMode(value: unknown): value is TableWidgetDisplayMode {
  return TABLE_WIDGET_DISPLAY_MODES.some((displayMode) => displayMode === value);
}

export function getTableWidgetVisibleStepCount(
  stepRange?: TableWidgetStepRange,
): number | undefined {
  const start = stepRange?.start;
  const end = stepRange?.end;

  if (start == null || end == null) {
    return undefined;
  }

  if (!Number.isInteger(start) || !Number.isInteger(end) || end < start) {
    return undefined;
  }

  return end - start + 1;
}

export function getTableWidgetVisibleSteps(stepRange?: TableWidgetStepRange): number[] {
  const visibleStepCount = getTableWidgetVisibleStepCount(stepRange);
  const start = stepRange?.start;

  if (visibleStepCount === undefined || start === undefined) {
    return [];
  }

  return Array.from({ length: visibleStepCount }, (_, index) => start + index);
}

export function getTableWidgetStepRangeValidationError(
  stepRange?: TableWidgetStepRange,
): string | undefined {
  if (!stepRange || stepRange.start == null || stepRange.end == null) {
    return 'Step range start and end are required.';
  }

  if (!Number.isInteger(stepRange.start) || !Number.isInteger(stepRange.end)) {
    return 'Step range values must be whole numbers.';
  }

  if (stepRange.start < 0 || stepRange.end < 0) {
    return 'Step range values must be non-negative.';
  }

  if (stepRange.end < stepRange.start) {
    return 'Step range end must be greater than or equal to start.';
  }

  const visibleStepCount = getTableWidgetVisibleStepCount(stepRange);

  if (visibleStepCount && visibleStepCount > TABLE_WIDGET_MAX_VISIBLE_STEPS) {
    return `Step range can include at most ${TABLE_WIDGET_MAX_VISIBLE_STEPS} visible steps.`;
  }

  return undefined;
}

export function isTableWidgetStepRangeValid(
  stepRange?: TableWidgetStepRange,
): stepRange is Required<TableWidgetStepRange> {
  return getTableWidgetStepRangeValidationError(stepRange) === undefined;
}
